import { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

// import useWindowSize from '../../hooks/useWindowSize.js';

import LdsIcon from '../LdsIcon/index.jsx';

function LdsStepIndicator({
  id,
  steps,
  activeIndex = 0,
  checkIconLabel = 'Complete',
  stepIndicatorStep = 'Step',
  stepIndicatorOf = 'of',
  forceCondensedMode = false,
  className,
  ...rest
}) {
  const indicatorRef = useRef(null);
  const initializedRef = useRef(false);

  // Responsive display mode state handler
  const [condensed, setCondensed] = useState(false);

  // Number of steps and step labels state handler
  const [stepsArray, setStepsArray] = useState([]);

  // Percentage complete state handler
  const [percentComplete, setPercentComplete] = useState(0);

  // Determine the total number of steps from the steps prop input
  const stepCount = typeof steps === 'number' ? steps : steps.length;

  /**
   * CHECK WIDTH WATCHER EFFECT
   *
   * This effect initially checks the width of the component and sets the correct responsive
   * display mode.
   *
   * Using a ResizeObserver, it is watched for anytime its own width changes to see if it needs to
   * update the responsive display mode. It is necessary to do this by watching the component's
   * width for cases where the component may be in an initially hidden container which may have
   * no width. When its container's visibility state changes it may resize and need to update the
   * responsive display mode. (i.e. when used in a modal or a tab panel)
   */
  useEffect(() => {
    /* This function determines if the "bubble" mode can be displayed by computing the number of
     * steps times the width of the bubbles to get a minimum width for the current step indicator.
     * If the current width of the indicator is not wide enough to show all the steps in the
     * "bubble" mode it switches to the "dots" or "condensed" mode. This will happen as necessary
     * where the condensed "dots" mode is not specifically for "mobile" display nor is the "bubble"
     * mode specifically for "desktop" display.
     */
    const checkWidth = () => {
      if (forceCondensedMode) {
        setCondensed(true);
      } else {
        const minStepWidth = (stepCount * 32) + ((stepCount - 1) * 32);
        const indicatorWidth = indicatorRef.current.getBoundingClientRect().width;
        setCondensed(minStepWidth > indicatorWidth);
      }
    };

    // Initialize the step indicator width, do this only once
    if (!initializedRef.current) {
      checkWidth();
      initializedRef.current = true;
    }

    // If the indicator ITSELF resizes for any reason run the check width
    const resizeObserver = new ResizeObserver(() => {
      checkWidth();
    });
    resizeObserver.observe(indicatorRef.current);

    return () => {
      if (forceCondensedMode) resizeObserver.disconnect();
    };
  }, [forceCondensedMode, stepCount]);

  /**
   * STEPS AND STEP STATUS EFFECT
   *
   * This effect sets the steps array from the steps prop input whether it is anumber or array of
   * step labels, the count of which implies the number of steps.
   *
   * It also sets the percentage complete (how many steps of the total are completed)
   */
  useEffect(() => {
    const stepType = typeof steps;
    const newSteps = stepType === 'number' ? Array.from(Array(steps).keys(), (_, i) => i + 1) : steps;
    const percentage = (activeIndex / (newSteps.length - 1)) * 100;
    setStepsArray(newSteps);
    setPercentComplete(percentage < 100 ? percentage : 100);
  }, [steps, activeIndex]);

  return (
    <div
      role="region"
      id={id}
      ref={indicatorRef}
      className={cn(
        'lds-step-indicator',
        {
          condensed,
        },
        className
      )}
      aria-live="polite"
      aria-label={`${stepIndicatorStep} ${activeIndex + 1} ${stepIndicatorOf} ${stepCount}`}
      {...rest}
    >
      <div className="lds-step-indicator-lines">
        <div className="lds-step-indicator-line-default" />
        <div
          className="lds-step-indicator-line-complete"
          style={{ width: `${percentComplete}%` }}
        />
      </div>
      <ol role="list">
        {
          stepsArray.map((step, index) => (
            <li
              id={`step-indicator-dots-${index + 1}`}
              key={`step-indicator-dots-${index}`}
              className={cn(
                {
                  active: index === activeIndex,
                  completed: index < activeIndex,
                }
              )}
              aria-current={ (index === activeIndex) ? `${stepIndicatorStep}` : null}
            >
              <span className={ (index < activeIndex) ? 'sr-only' : null}>
                {step}
              </span>
              {
                (index < activeIndex)
                && <LdsIcon name="Check" label={checkIconLabel} className="lds-step-indicator-checkmark" inline/>
              }
            </li>
          ))
        }
      </ol>
      <div className="lds-step-indicator-step-count">
        <span
          className="sr-only"
        >
          {`${stepIndicatorStep}`}
        </span>
        {`${activeIndex + 1 } ${stepIndicatorOf} ${stepCount}`}
      </div>
    </div>
  );
}
export default LdsStepIndicator;

LdsStepIndicator.propTypes = {
  id: PropTypes.string.isRequired,
  steps: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]).isRequired,
  activeIndex: PropTypes.number,
  checkIconLabel: PropTypes.string,
  stepIndicatorStep: PropTypes.string,
  stepIndicatorOf: PropTypes.string,
  forceCondensedMode: PropTypes.bool,
  className: PropTypes.string,
  rest: PropTypes.any,
};
